import { useEffect, useState } from "react";
import QRCodeStyling from "qr-code-styling";
import { useTranslation } from "react-i18next";
import { displayToast } from "../../../components/app/AppToast";
import BottomPanel from "../../../components/app/panels/BottomPanel";
import { Button } from "../../../components/ui/Button";
import { shareImage } from "../../app/utils/shareImage";
import { apiClient } from "../../../services/api";
import { REDIRECT_HOST } from "../../../config";
import { env } from "../../../config/env";
import { isNativeApp } from "../../../common/hooks/useIsDesktopView";
import { AnalyticsItemList } from "../../../tools/analytics/analytics.types";
import type { PlaylistForAnalytics } from "../../../tools/analytics/domains/playlist.analytics";
import { useSharePlaylist } from "./useSharePlaylist";

export type UseSharePlaylistQr = {
  triggerShare: () => Promise<void>;
  triggerNativeShare: () => Promise<void>;
  renderShareSheet: () => React.JSX.Element | null;
};

async function generateQrCodeUrl(url: string): Promise<string | null> {
  const qrCode = new QRCodeStyling({
    data: url,
    width: 512,
    height: 512,
    dotsOptions: { type: "dots" },
    image:
      env.appEnv === "production"
        ? "/juisci-logo-tube.png"
        : "/juisci-logo-tube-dev.png",
    imageOptions: {
      imageSize: 0.4,
      margin: 4,
      crossOrigin: "anonymous",
    },
  });
  const raw = await qrCode.getRawData("png");
  if (!(raw instanceof Blob)) return null;
  return URL.createObjectURL(raw);
}

export function useSharePlaylistQr(
  playlist: PlaylistForAnalytics | null,
  ownerId: string | null
): UseSharePlaylistQr {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const { triggerShare: triggerNativeShare } = useSharePlaylist(
    null,
    playlist,
    AnalyticsItemList.PROFILE
  );

  useEffect(() => {
    return () => {
      if (qrCodeUrl !== null) URL.revokeObjectURL(qrCodeUrl);
    };
  }, [qrCodeUrl]);

  const close = (): void => {
    setIsOpen(false);
  };

  const triggerShare = async (): Promise<void> => {
    const genericError = t("Something went wrong. Please try again.");
    try {
      if (playlist === null || ownerId === null) return;

      // Create a tracked short-link (Redirect) server-side so this QR is iso
      // with the other dashboard QR codes (scans tracked, surfaced in the
      // "Field Force Playlist" filter). The QR encodes the short-link.
      const { body: code } = await apiClient.post(
        "/webapp/private/playlists/:playlistId/field-force-share-link",
        { param: { playlistId: playlist.id } }
      );
      const url = `https://${REDIRECT_HOST}/${code}`;
      const generated = await generateQrCodeUrl(url);
      if (generated === null) {
        displayToast(genericError, "error");
        return;
      }

      setQrCodeUrl(generated);
      setIsOpen(true);
    } catch {
      displayToast(genericError, "error");
    }
  };

  const handleDownloadQrCode = async (): Promise<void> => {
    if (qrCodeUrl === null || playlist === null) return;
    try {
      const response = await fetch(qrCodeUrl);
      const blob = await response.blob();
      await shareImage(
        blob,
        `${playlist.title === "" ? "playlist" : playlist.title}-qr-code.png`,
        t("QR code downloaded")
      );
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  };

  const renderShareSheet = (): React.JSX.Element | null => {
    return (
      <BottomPanel
        isOpen={isOpen}
        onClose={close}
        height={isNativeApp() ? "83dvh" : "90dvh"}
        displayCloseButton
      >
        {qrCodeUrl === null || playlist === null ? null : (
          <div className="flex flex-1 flex-col justify-between gap-8 px-5 pt-8 pb-8">
            <div className="flex flex-col gap-4">
              <img
                className="mx-auto w-5/7 max-w-[30dvh]"
                src={qrCodeUrl}
                alt={t("QR Code")}
              />
              <div className="text-center text-2xl/tight font-bold">
                {playlist.title}
              </div>
              <div className="text-center text-base/normal font-normal text-secondary-bluegrey-4">
                {t(
                  "Have your peers scan this code to add the playlist to their shared playlists."
                )}
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <Button
                variant="outline"
                size="md"
                onClick={() => {
                  void handleDownloadQrCode();
                }}
              >
                {t("Download QR code")}
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={() => {
                  void triggerNativeShare();
                }}
              >
                {t("Share this playlist")}
              </Button>
            </div>
          </div>
        )}
      </BottomPanel>
    );
  };

  return { triggerShare, triggerNativeShare, renderShareSheet };
}
